import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Friedhof, CreateFriedhofRequest, UpdateFriedhofRequest, FRIEDHOF_STATUS } from '../types';
import friedhofService from '../services/friedhofService';
import { useAuth } from '../contexts/AuthContext';
import { useFriedhof } from '../contexts/FriedhofContext';
import { ankunft, markiereNeu } from '../utils/ankunft';
import {
  Button, Badge, PageHeader, Modal, Field, Input, Select, Textarea, FormActions,
  LoadingScreen, EmptyState, RowAction, Icon, tableCard, tableScroll, th, td, tableRow,
} from '../components/ui';

const statusLabels: Record<string, string> = {
  AKTIV: 'Aktiv', INAKTIV: 'Inaktiv', VOLL_BELEGT: 'Voll belegt'
};
const statusColors: Record<string, string> = {
  AKTIV: 'bg-green-100 text-green-800', INAKTIV: 'bg-gray-100 text-gray-700',
  VOLL_BELEGT: 'bg-red-100 text-red-800'
};

const emptyForm = { name: '', adresse: '', anzahlGrabstaetten: '', status: 'AKTIV', notizen: '', active: true };

const Friedhoefe: React.FC = () => {
  const { isAdmin } = useAuth();
  const { setAnzahlFriedhoefe } = useFriedhof();
  const [items, setItems] = useState<Friedhof[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Friedhof | null>(null);
  const [formData, setFormData] = useState<any>(emptyForm);

  // Einmal beim Öffnen laden.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const liste = await friedhofService.getAll();
      setItems(liste);
      setAnzahlFriedhoefe(liste.length); // „Friedhof wechseln“ erscheint/verschwindet mit
    }
    catch { toast.error('Friedhöfe konnten nicht geladen werden'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = {
        name: formData.name,
        adresse: formData.adresse,
        anzahlGrabstaetten: parseInt(formData.anzahlGrabstaetten) || 0,
        status: formData.status,
        notizen: formData.notizen || undefined,
        active: formData.active
      };
      if (editing) {
        await friedhofService.update(editing.id, data as UpdateFriedhofRequest);
        markiereNeu(`friedhof-${editing.id}`);
        toast.success('Friedhof gespeichert');
      } else {
        const neu = await friedhofService.create(data as CreateFriedhofRequest);
        markiereNeu(`friedhof-${neu.id}`);
        toast.success('Friedhof angelegt');
      }
      resetForm(); await load();
    } catch (error: any) { toast.error(error?.response?.data?.error || 'Friedhof konnte nicht gespeichert werden'); }
  };

  const handleEdit = (item: Friedhof) => {
    setEditing(item);
    setFormData({
      name: item.name, adresse: item.adresse,
      anzahlGrabstaetten: item.anzahlGrabstaetten.toString(),
      status: item.status, notizen: item.notizen || '', active: item.active
    });
    setShowForm(true);
  };

  const handleDelete = async (item: Friedhof) => {
    if (window.confirm(`Friedhof „${item.name}“ löschen?\nEr verschwindet danach aus der Friedhofsauswahl.`)) {
      try { await friedhofService.delete(item.id); toast.success('Friedhof gelöscht'); await load(); }
      catch (error: any) { toast.error(error?.response?.data?.error || 'Friedhof konnte nicht gelöscht werden'); }
    }
  };

  const resetForm = () => { setFormData(emptyForm); setEditing(null); setShowForm(false); };

  if (loading) return <LoadingScreen />;

  return (
    <div className="mx-auto max-w-6xl p-6">
      <PageHeader
        title="Friedhöfe"
        subtitle="Alle verwalteten Friedhöfe im Überblick."
        actions={isAdmin() && (
          <Button onClick={() => setShowForm(true)} icon={<Icon.Plus className="h-4 w-4" />}>Friedhof anlegen</Button>
        )}
      />

      {showForm && (
        <Modal title={editing ? 'Friedhof bearbeiten' : 'Neuer Friedhof'} onClose={resetForm}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Field label="Name" required>
              <Input required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
            </Field>
            <Field label="Adresse" required>
              <Textarea required rows={2} value={formData.adresse} onChange={e => setFormData({ ...formData, adresse: e.target.value })} />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Anzahl Grabstätten" required>
                <Input type="number" min={0} required value={formData.anzahlGrabstaetten} onChange={e => setFormData({ ...formData, anzahlGrabstaetten: e.target.value })} />
              </Field>
              <Field label="Status" required>
                <Select value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}>
                  {FRIEDHOF_STATUS.map(s => <option key={s} value={s}>{statusLabels[s]}</option>)}
                </Select>
              </Field>
            </div>
            <Field label="Notizen">
              <Textarea rows={2} value={formData.notizen} onChange={e => setFormData({ ...formData, notizen: e.target.value })} />
            </Field>
            {editing && (
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={formData.active} onChange={e => setFormData({ ...formData, active: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                Aktiv
              </label>
            )}
            <FormActions>
              <Button type="button" variant="secondary" onClick={resetForm}>Abbrechen</Button>
              <Button type="submit">{editing ? 'Speichern' : 'Anlegen'}</Button>
            </FormActions>
          </form>
        </Modal>
      )}

      <div className={tableCard}>
        <div className={tableScroll}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50/80">
              <tr>
                <th className={th}>Name</th>
                <th className={`${th} hidden lg:table-cell`}>Adresse</th>
                <th className={`${th} hidden md:table-cell`}>Grabstätten</th>
                <th className={th}>Status</th>
                <th className={`${th} text-right`}>Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map(item => { const a = ankunft(`friedhof-${item.id}`); return (
                <tr key={item.id} ref={a.ref} className={`${tableRow} ${a.className}`}>
                  <td className={`${td} whitespace-nowrap font-medium text-gray-900`}>{item.name}</td>
                  <td className={`${td} text-gray-500 hidden lg:table-cell`}>{item.adresse}</td>
                  <td className={`${td} whitespace-nowrap text-gray-500 hidden md:table-cell`}>{item.anzahlGrabstaetten}</td>
                  <td className={`${td} whitespace-nowrap`}><Badge className={statusColors[item.status] || ''}>{statusLabels[item.status] || item.status}</Badge></td>
                  <td className={`${td} whitespace-nowrap text-right`}>
                    <div className="flex items-center justify-end gap-1">
                      <Link to={`/lageplan/${item.id}`} className="rounded-md px-2 py-1 text-sm font-medium text-brand-700 transition-colors hover:bg-brand-50">Lageplan</Link>
                      {isAdmin() && <RowAction onClick={() => handleEdit(item)}>Bearbeiten</RowAction>}
                      {isAdmin() && <RowAction danger onClick={() => handleDelete(item)}>Löschen</RowAction>}
                    </div>
                  </td>
                </tr>
              ); })}
            </tbody>
          </table>
        </div>
        {items.length === 0 && (
          <EmptyState
            icon={<Icon.MapPin />}
            title="Noch keine Friedhöfe"
            hint={isAdmin() ? 'Jeder Friedhof bekommt seinen eigenen Lageplan mit Gräbern.' : 'Friedhöfe legt ein Administrator an.'}
            action={isAdmin() && (
              <Button onClick={() => setShowForm(true)} icon={<Icon.Plus className="h-4 w-4" />}>Friedhof anlegen</Button>
            )}
          />
        )}
      </div>
    </div>
  );
};

export default Friedhoefe;
